import React, { useEffect, useRef } from 'react'
import styles from './EmailDispatchProgress.module.css'

const EmailDispatchProgress = ({
  open = true,
  fullscreen = false,
  batchSize,
  processed,
  total,
  sent,
  failed = 0,
  running = true,
  starting = false,
  phase = 'sending',
}) => {
  const safeTotal = Math.max(0, total || 0)
  const safeProcessed = Math.max(0, processed ?? 0)
  const displayCurrent = running || starting
    ? Math.min(safeProcessed, safeTotal || safeProcessed)
    : safeTotal
  const pct = safeTotal > 0 ? Math.round((displayCurrent / safeTotal) * 100) : 0

  const counterRef = useRef(null)
  const prevProcessed = useRef(safeProcessed)

  useEffect(() => {
    if (!fullscreen || !open) return undefined
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prevOverflow
    }
  }, [fullscreen, open])

  useEffect(() => {
    if (displayCurrent !== prevProcessed.current && counterRef.current) {
      counterRef.current.classList.remove(styles.counterPulse)
      void counterRef.current.offsetWidth
      counterRef.current.classList.add(styles.counterPulse)
      prevProcessed.current = displayCurrent
    }
  }, [displayCurrent])

  if (!open) return null

  const panel = (
    <>
      {!fullscreen && running && (
        <div className={styles.stickyBar} role="status" aria-live="polite">
          <span className={styles.stickyLabel}>Envoi email en direct</span>
          <strong className={styles.stickyCounter}>
            {displayCurrent} / {safeTotal || '…'}
          </strong>
        </div>
      )}

      <section
        className={`${styles.card} ${fullscreen ? styles.cardFullscreen : ''}`}
        role="status"
        aria-live="polite"
      >
        <div className={styles.header}>
          <div className={styles.headerText}>
            <h3>{fullscreen ? 'Envoi des factures aux coaches' : 'Distribution des factures par email'}</h3>
            <p>
              {starting
                ? 'Démarrage de l\'envoi…'
                : running
                  ? `Envoi en cours — le compteur passe de 1/${safeTotal || '…'} à ${safeTotal || '…'}/${safeTotal || '…'}.`
                  : 'Envoi terminé — vous pouvez continuer.'}
            </p>
          </div>
          {(running || starting) && (
            <span className={styles.livePill}>
              <span className={styles.liveDot} aria-hidden="true" />
              En direct
            </span>
          )}
        </div>

        <div className={styles.heroCounterWrap}>
          <span className={styles.heroLabel}>Progression envoi</span>
          <div ref={counterRef} className={styles.heroCounter}>
            <span className={styles.heroCurrent}>{displayCurrent}</span>
            <span className={styles.heroSep}>/</span>
            <span className={styles.heroTotal}>{safeTotal || '…'}</span>
          </div>
          <span className={styles.heroHint}>
            {running || starting
              ? `${sent ?? 0} envoyé(s) · ${failed} échec(s)`
              : `${displayCurrent} / ${safeTotal} — terminé`}
          </span>
        </div>

        <div className={styles.progressWrap}>
          <div className={styles.progressMeta}>
            <span>Avancement</span>
            <strong>{pct}%</strong>
          </div>
          <div className={styles.track} aria-hidden="true">
            <div className={styles.fill} style={{ width: `${pct}%` }} />
          </div>
        </div>

        <div className={styles.stats}>
          <div className={styles.stat}>
            <span>Étape</span>
            <strong>{displayCurrent}{safeTotal ? ` / ${safeTotal}` : ''}</strong>
          </div>
          <div className={styles.stat}>
            <span>Envoyés</span>
            <strong>{sent ?? 0}</strong>
          </div>
          <div className={styles.stat}>
            <span>Échecs</span>
            <strong>{failed}</strong>
          </div>
          <div className={styles.stat}>
            <span>Lot</span>
            <strong>{batchSize}</strong>
          </div>
        </div>

        {(running || starting) && (
          <p className={styles.hint}>
            {fullscreen
              ? 'Ne fermez pas cet onglet et n\'utilisez pas le menu pendant l\'envoi. L\'écran se déverrouillera à '
              : 'Ne redémarrez pas le serveur pendant l\'envoi. Le compteur avance à chaque email traité '}
            {safeTotal ? `${safeTotal}/${safeTotal}.` : 'la fin.'}
          </p>
        )}
      </section>
    </>
  )

  if (fullscreen) {
    return (
      <div className={styles.fullscreenLock} role="alertdialog" aria-modal="true" aria-busy={running || starting}>
        <div className={styles.fullscreenPanel}>{panel}</div>
      </div>
    )
  }

  return panel
}

export default EmailDispatchProgress
